<div class="header">
  <app-section-menu
    [menuItems]="sectionMenuItems"
    [scrollContainerId]="'merge-grid-scroll-container'"
    [scrollOffset]="96"
  />
</div>
<!-- Main table structure for the merge view -->
<article class="merge-section-details">
  <div class="merge-grid-container" id="merge-grid-scroll-container">
    <table class="merge-grid">
      <tbody>
        <!-- Top header row (sticky) -->
        <tr class="top-header-row">
          <td></td>
          @for (col of topHeaderColumns; track $index) {
            <td>
              <div class="cell-container">
                <div class="incident-id">{{ col.incidentId }}</div>
                <div
                  [ngClass]="{
                    'incident-merged': col.label.toLowerCase() === 'merged',
                    'incident-original': col.label.toLowerCase() === 'original',
                    'incident-duplicate': col.label.toLowerCase() === 'duplicate',
                  }"
                >
                  {{ col.label }}
                </div>
                @if (col.label.toLowerCase() === 'duplicate') {
                  <button class="actions-button" matIconButton [matMenuTriggerFor]="actionsMenu">
                    <mat-icon>more_vert</mat-icon>
                  </button>
                  <mat-menu class="actions-menu-buttons" xPosition="before" yPosition="below" #actionsMenu="matMenu">
                    <button mat-menu-item (click)="detachIncidentAsync(col.incidentId)">
                      <mat-icon>call_split</mat-icon>Detach incident
                    </button>
                  </mat-menu>
                }
              </div>
            </td>
          }
        </tr>

        <!-- Details -->
        <ng-container
          *ngTemplateOutlet="mergeSectionHeaderTemplate; context: { sectionId: 'details', title: 'Details' }"
        ></ng-container>

        <ng-container *ngTemplateOutlet="tableTemplate; context: { rows: detailsRows }"></ng-container>

        <!-- Affected Entity -->
        <ng-container
          *ngTemplateOutlet="
            mergeSectionHeaderTemplate;
            context: { sectionId: 'affected-entity', title: 'Affected Entity' }
          "
        ></ng-container>

        <ng-container *ngTemplateOutlet="tableTemplate; context: { rows: entityRows }"></ng-container>

        <!-- Rims -->
        <ng-container
          *ngTemplateOutlet="mergeSectionHeaderTemplate; context: { sectionId: 'rims', title: 'RIMS' }"
        ></ng-container>

        @for (rimsPhaseSection of rimsPhaseSections; track rimsPhaseSection.phaseId) {
          <ng-container
            *ngTemplateOutlet="rimsPhaseSubsectionHeaderTemplate; context: { title: rimsPhaseSection.phaseName }"
          ></ng-container>

          @for (rimsStep of rimsPhaseSection.steps; track rimsStep.stepId) {
            <ng-container
              *ngTemplateOutlet="
                rimsStepExpandableSectionTemplate;
                context: {
                  stepId: rimsStep.stepId,
                  stepName: rimsStep.stepName,
                  rows: rimsStepRows[rimsStep.stepId] ?? [],
                }
              "
            ></ng-container>
          }
        }

        <!-- Threat Actor -->
        <ng-container
          *ngTemplateOutlet="mergeSectionHeaderTemplate; context: { sectionId: 'threat-actor', title: 'Threat Actor' }"
        ></ng-container>

        <ng-container *ngTemplateOutlet="tableTemplate; context: { rows: threatActorRows }"></ng-container>

        <!-- References -->
        <ng-container
          *ngTemplateOutlet="mergeSectionHeaderTemplate; context: { sectionId: 'references', title: 'References' }"
        ></ng-container>

        <ng-container
          *ngTemplateOutlet="referenceSubsectionHeaderTemplate; context: { title: 'Article References' }"
        ></ng-container>

        <ng-container
          *ngTemplateOutlet="staircaseTableTemplate; context: { rows: articleReferenceRows, headerPrefix: 'Article' }"
        ></ng-container>

        <ng-container
          *ngTemplateOutlet="referenceSubsectionHeaderTemplate; context: { title: 'MediaReferences' }"
        ></ng-container>

        <ng-container
          *ngTemplateOutlet="staircaseTableTemplate; context: { rows: mediaReferenceRows, headerPrefix: 'File' }"
        ></ng-container>
      </tbody>
    </table>
  </div>
</article>

<!-- Column cell templates -->
<ng-template #columnCellTemplate let-row="row" let-columnCell="columnCell" let-columnIndex="columnIndex">
  @switch (row.type) {
    @case ('text') {
      <div class="column-cell-text">{{ columnCell.value ?? '-' }}</div>
    }

    @case ('text-expandable') {
      <div class="column-cell-text-expandable">
        <div class="column-cell-text-expandable_text" [class.expanded]="row.isExpanded" #textExpandableContent>
          {{ columnCell.value ?? '-' }}
        </div>
        @if (columnCell.isOverflowing) {
          <button class="column-cell-text-expandable_btn" (click)="toggleExpand(row)">
            {{ row.isExpanded ? 'Show less' : 'Show all' }}
            <mat-icon class="button-expand-icon">{{ row.isExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        }
      </div>
    }

    @case ('article') {
      @if (asArticleReferenceModel(columnCell.value); as articleValue) {
        <a class="column-cell-website" [href]="articleValue.url" target="_blank"
          ><span>{{ articleValue.url }}</span></a
        >
      }
    }

    @case ('media') {
      @if (asMediaReferenceMetadataOrCreate(columnCell.value); as mediaValue) {
        <div class="reference-container">
          <button class="text-content" (click)="mediaReferenceHelper.previewMedia(mediaValue.id)" tabindex="0">
            <mat-icon class="link-icon">{{ mediaReferenceHelper.mapContentTypeIcon(mediaValue.contentType) }}</mat-icon>
            @if (mediaValue.id) {
              <span>File {{ mediaValue.id }}</span>
            } @else {
              <span>File (new)</span>
            }
          </button>
        </div>
      }
    }

    @case ('website') {
      @if (!columnCell.value) {
        <div class="column-cell-text">{{ columnCell.value ?? '-' }}</div>
      } @else {
        <a class="column-cell-website" [href]="columnCell.value" target="_blank"
          ><span>{{ columnCell.value }}</span></a
        >
      }
    }

    @case ('slide-toggle') {
      @if (asSlideToggleValue(columnCell.value) !== null) {
        <div class="column-cell-checkbox">
          <mat-slide-toggle
            class="slide-toggle"
            [disabled]="true"
            [checked]="asSlideToggleValue(columnCell.value) ?? false"
          ></mat-slide-toggle>
          <div>Reviewed</div>
        </div>
      }
    }

    @case ('country') {
      @if (asCountryValue(columnCell.value); as countryValue) {
        <div class="column-cell-country">
          <span class="country-icon"> {{ CountryFlags[countryValue.countryCode] }}</span>
          <div>{{ CountryDescriptor[countryValue.countryCode || 'Unknown'] }}</div>
        </div>
      }
    }
  }
</ng-template>

<!-- Table template -->
<ng-template #tableTemplate let-rows="rows">
  @for (row of asMergeRows(rows); track $index) {
    <ng-container *ngTemplateOutlet="mergeRowTemplate; context: { row: row }"></ng-container>
  }
</ng-template>

<!-- Standard merge row template -->
<ng-template #mergeRowTemplate let-row="row">
  <tr>
    <td class="field-label">
      <div class="field-main-container">
        <mat-icon [style.visibility]="row.hasConflict ? 'visible' : 'hidden'">error</mat-icon>
        <div class="text-area" [style.padding-left.px]="calculateHeaderDepth(2, row.depth)">
          <div>{{ row.displayName }}</div>
          <div class="description-text">{{ row.description }}</div>
        </div>
      </div>
    </td>

    @for (columnCell of row.columns; track $index) {
      <td
        [class.merge-cell-selected]="columnCell.isChecked ?? false"
        [class.merge-cell-step-not-included]="columnCell.isStepNotIncluded ?? false"
      >
        <div class="merge-cell-layout">
          <div class="merge-cell-content">
            <ng-container
              *ngTemplateOutlet="columnCellTemplate; context: { row: row, columnCell: columnCell, columnIndex: $index }"
            ></ng-container>
          </div>

          @if (
            (rowSelectionCheckboxVisibility.get(row) ?? false) && $index > 0 && !(columnCell.isStepNotIncluded ?? false)
          ) {
            <div class="merge-cell-action">
              <input
                type="checkbox"
                class="merge-row-selection-checkbox"
                [checked]="columnCell.isChecked ?? false"
                (change)="onRowSelectionCheckboxChange(row, $index, $event)"
              />
            </div>
          }
        </div>
      </td>
    }
  </tr>
</ng-template>

<!-- Recursive RIMS step rows template -->
<ng-template #rimsStepRowsTemplate let-rows="rows">
  @for (rimsStepRow of rows; track rimsStepRow.kind !== 'field' ? rimsStepRow.field : $index) {
    @if (rimsStepRow.kind === 'array-item') {
      <ng-container
        *ngTemplateOutlet="rimsStepArrayItemExpandableSectionTemplate; context: { itemRow: rimsStepRow }"
      ></ng-container>
    } @else if (rimsStepRow.kind !== 'field') {
      <ng-container
        *ngTemplateOutlet="rimsStepObjectExpandableSectionTemplate; context: { objectRow: rimsStepRow }"
      ></ng-container>
    } @else {
      <ng-container *ngTemplateOutlet="mergeRowTemplate; context: { row: rimsStepRow.row }"></ng-container>
    }
  }
</ng-template>

<!-- RIMS step object expandable section template -->
<ng-template #rimsStepObjectExpandableSectionTemplate let-objectRow="objectRow">
  <tr
    class="rims-step-object-expandable-section-row"
    [class.is-expanded]="isRimsStepNestedSectionExpanded(objectRow.field)"
    role="button"
    [attr.aria-expanded]="isRimsStepNestedSectionExpanded(objectRow.field)"
    tabindex="0"
    (keydown.enter)="toggleRimsStepNestedSectionExpand(objectRow.field)"
    (keydown.space)="$event.preventDefault(); toggleRimsStepNestedSectionExpand(objectRow.field)"
  >
    <td
      class="field-label rims-step-object-expandable-section-row-title-cell"
      [style.padding-left.px]="calculateHeaderDepth(24, objectRow.depth)"
      (click)="toggleRimsStepNestedSectionExpand(objectRow.field)"
    >
      <div class="rims-step-object-expandable-section-row-title-cell-content">
        <mat-icon>
          {{ isRimsStepNestedSectionExpanded(objectRow.field) ? 'keyboard_arrow_down' : 'keyboard_arrow_right' }}
        </mat-icon>
        <span>{{ objectRow.displayName }}</span>
      </div>
    </td>
    <td
      class="rims-step-object-expandable-section-row-fill-cell"
      [attr.colspan]="topHeaderColumns.length"
      (click)="toggleRimsStepNestedSectionExpand(objectRow.field)"
    ></td>
  </tr>

  @if (isRimsStepNestedSectionExpanded(objectRow.field)) {
    <ng-container *ngTemplateOutlet="rimsStepRowsTemplate; context: { rows: objectRow.children }"></ng-container>
  }
</ng-template>

<!-- RIMS step array-item (set) expandable section template: adds one checkbox per matched incident column instead of a single fill cell. -->
<ng-template #rimsStepArrayItemExpandableSectionTemplate let-itemRow="itemRow">
  <tr
    class="rims-step-object-expandable-section-row"
    [class.is-expanded]="isRimsStepNestedSectionExpanded(itemRow.field)"
    role="button"
    [attr.aria-expanded]="isRimsStepNestedSectionExpanded(itemRow.field)"
    tabindex="0"
    (keydown.enter)="toggleRimsStepNestedSectionExpand(itemRow.field)"
    (keydown.space)="$event.preventDefault(); toggleRimsStepNestedSectionExpand(itemRow.field)"
  >
    <td
      class="field-label rims-step-object-expandable-section-row-title-cell"
      [style.padding-left.px]="calculateHeaderDepth(24, itemRow.depth)"
      (click)="toggleRimsStepNestedSectionExpand(itemRow.field)"
    >
      <div class="rims-step-object-expandable-section-row-title-cell-content">
        <mat-icon>
          {{ isRimsStepNestedSectionExpanded(itemRow.field) ? 'keyboard_arrow_down' : 'keyboard_arrow_right' }}
        </mat-icon>
        <span>{{ itemRow.displayName }}</span>
      </div>
    </td>

    @if (isRimsSetItemMatched(itemRow)) {
      @for (columnCell of itemRow.columns; track $index) {
        <td
          class="rims-step-array-item-column-cell"
          [class.merge-cell-selected]="columnCell.isChecked ?? false"
          (click)="toggleRimsStepNestedSectionExpand(itemRow.field)"
        >
          @if ($index > 0 && columnCell.hasValue) {
            <div class="merge-cell-action">
              <input
                type="checkbox"
                class="merge-row-selection-checkbox"
                [checked]="columnCell.isChecked ?? false"
                (click)="$event.stopPropagation()"
                (change)="onRimsSetCheckboxSelected(itemRow, $index, $event)"
              />
            </div>
          }
        </td>
      }
    } @else {
      <td
        class="rims-step-object-expandable-section-row-fill-cell"
        [attr.colspan]="topHeaderColumns.length"
        (click)="toggleRimsStepNestedSectionExpand(itemRow.field)"
      ></td>
    }
  </tr>

  @if (isRimsStepNestedSectionExpanded(itemRow.field)) {
    <ng-container *ngTemplateOutlet="rimsStepRowsTemplate; context: { rows: itemRow.children }"></ng-container>
  }
</ng-template>

<!-- Staircase table template -->
<ng-template #staircaseTableTemplate let-rows="rows" let-headerPrefix="headerPrefix">
  @for (row of asStaircaseRows(rows); track $index) {
    <tr>
      <td class="field-label">
        <div class="field-main-container">
          <mat-icon [style.visibility]="row.hasConflict ? 'visible' : 'hidden'">error</mat-icon>
          <div class="text-area">
            @if (headerPrefix) {
              <div>{{ headerPrefix }} {{ $index + 1 }}</div>
            } @else {
              <div>{{ row.displayName }}</div>
            }
            <div class="description-text">{{ row.description }}</div>
          </div>
        </div>
      </td>

      @if (isSingleColumnMergeRow(row)) {
        @for (_ of topHeaderColumns; track $index; let colIndex = $index) {
          <td [class.merge-cell-selected]="colIndex === row.columnIndex && (row.column.isChecked ?? false)">
            @if (colIndex === row.columnIndex) {
              <div class="merge-cell-layout">
                <div class="merge-cell-content">
                  <ng-container
                    *ngTemplateOutlet="
                      columnCellTemplate;
                      context: { row: row, columnCell: row.column, columnIndex: colIndex }
                    "
                  ></ng-container>
                </div>

                @if (colIndex > 0 && row.field) {
                  <div class="merge-cell-action">
                    <input
                      type="checkbox"
                      class="merge-row-selection-checkbox"
                      [checked]="row.column.isChecked ?? false"
                      (change)="onReferenceSelectionCheckboxChange(row, $event)"
                    />
                  </div>
                }
              </div>
            }
          </td>
        }
      } @else {
        <!-- Grouped row: same media id matched across incidents, always non-mergeable (no checkbox). -->
        @for (columnCell of row.columns; track $index) {
          <td>
            @if (columnCell.value !== null) {
              <div class="merge-cell-layout">
                <div class="merge-cell-content">
                  <ng-container
                    *ngTemplateOutlet="
                      columnCellTemplate;
                      context: { row: row, columnCell: columnCell, columnIndex: $index }
                    "
                  ></ng-container>
                </div>
              </div>
            }
          </td>
        }
      }
    </tr>
  }
</ng-template>

<!-- Reference subsection header template -->
<ng-template #referenceSubsectionHeaderTemplate let-title="title">
  <tr class="subsection-row">
    <td class="field-label subsection-title-cell">
      <h3 class="h3">{{ title }}</h3>
    </td>
    <td class="subsection-fill-cell" [attr.colspan]="topHeaderColumns.length"></td>
  </tr>
</ng-template>

<!-- Rims phase subsection header template -->
<ng-template #rimsPhaseSubsectionHeaderTemplate let-title="title">
  <tr class="rims-phase-section-row">
    <td class="field-label rims-phase-section-row-title-cell">
      <h3>{{ title }}</h3>
    </td>
    <td class="rims-phase-section-row-fill-cell" [attr.colspan]="topHeaderColumns.length"></td>
  </tr>
</ng-template>

<!-- Rims step expandable section template -->
<ng-template #rimsStepExpandableSectionTemplate let-stepId="stepId" let-stepName="stepName" let-rows="rows">
  <tr
    class="rims-step-expandable-section-row"
    [class.is-expanded]="isRimsStepExpanded(stepId)"
    role="button"
    [attr.aria-expanded]="isRimsStepExpanded(stepId)"
    tabindex="0"
    (keydown.enter)="toggleRimsStepExpand(stepId)"
    (keydown.space)="$event.preventDefault(); toggleRimsStepExpand(stepId)"
  >
    <td
      class="field-label rims-step-expandable-section-row-title-cell"
      [attr.colspan]="2"
      (click)="toggleRimsStepExpand(stepId)"
    >
      <div class="rims-step-expandable-section-row-title-cell-content">
        <mat-icon>{{ isRimsStepExpanded(stepId) ? 'keyboard_arrow_down' : 'keyboard_arrow_right' }}</mat-icon>
        <h4>{{ stepId }} - {{ stepName }}</h4>
      </div>
    </td>
    <td
      class="rims-step-expandable-section-row-fill-cell"
      [attr.colspan]="topHeaderColumns.length - 1"
      (click)="toggleRimsStepExpand(stepId)"
    ></td>
  </tr>

  @if (isRimsStepExpanded(stepId)) {
    <ng-container>
      <ng-container *ngTemplateOutlet="rimsStepRowsTemplate; context: { rows: rows }"></ng-container>
    </ng-container>
  }
</ng-template>

<!-- Section header template -->
<ng-template #mergeSectionHeaderTemplate let-sectionId="sectionId" let-title="title">
  <tr class="header-row" [attr.id]="sectionId">
    <td>
      <h2 class="h2">{{ title }}</h2>
    </td>
  </tr>
</ng-template>
